import React, { useEffect, useMemo, useRef, useState } from "react";
import { createGame, rollDice, applyMove, getValidMoves, hasValidMove, nextTurn } from "./engine.js";
import {
  ALL_COLORS, COLOR_HEX, COLOR_LABELS, PATH_GRID, HOME_COLS, BASE_CELLS,
  CENTER_CELLS, PLAYER_START, isSafeZone,
} from "./types.js";
import type { LadooGame, PlayerColor, CellPos } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type SetupStep = "setup" | "play";

export default function LadooPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [step, setStep] = useState<SetupStep>("setup");
  const [playerCount, setPlayerCount] = useState(2);
  const [cpuCount, setCpuCount] = useState(0);
  const [names, setNames] = useState<string[]>(["Player 1", "Player 2", "", ""]);
  const [game, setGame] = useState<LadooGame | null>(null);
  const [message, setMessage] = useState("");
  const [validMoves, setValidMoves] = useState<number[]>([]);
  const [capturedMsg, setCapturedMsg] = useState("");

  const cpuTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function startGame() {
    const g = createGame(playerCount, cpuCount);
    g.players.forEach((p, i) => {
      p.name = names[i]?.trim() || `Player ${i + 1}`;
    });
    setGame(g);
    setStep("play");
    setMessage(`${g.players[0].name}'s turn — roll the dice!`);
    setCapturedMsg("");
  }

  function handleRoll() {
    if (!game || game.rolled || game.winner !== null) return;
    const d = rollDice();
    const g = { ...game, dice: d, rolled: true, players: game.players };
    const moves = getValidMoves(g);
    setGame(g);
    setValidMoves(moves);
    setCapturedMsg("");
    if (moves.length === 0) {
      setMessage(`Rolled ${d} — no valid moves! Turn passes.`);
    } else {
      setMessage(`Rolled ${d}! Pick a token to move.`);
    }
  }

  function handleMove(tokenIndex: number) {
    if (!game) return;
    const result = applyMove(game, tokenIndex, game.dice!);
    setGame(result.game);
    setValidMoves([]);
    setCapturedMsg("");

    const msgs: string[] = [];
    if (result.landedHome) msgs.push("Token reached home!");
    if (result.captured.length > 0) {
      result.captured.forEach((tid) => {
        const victim = game.players.find((p) =>
          p.tokens.some((t) => t.id === tid && t.pos === -1)
        );
        if (victim) msgs.push(`Captured ${victim.name}'s token!`);
      });
    }

    if (result.game.winner !== null) {
      msgs.push(`${result.game.players[result.game.winner].name} wins!`);
    } else if (result.game.dice === null && !result.game.rolled) {
      msgs.push(`${result.game.players[result.game.turn].name}'s turn — roll!`);
    } else if (result.game.sixCount > 0) {
      msgs.push(`Rolled a 6! Roll again.`);
    }

    setMessage(msgs.join(" "));
  }

  function handleAutoPass() {
    if (!game) return;
    const g = { ...game, dice: null, rolled: false, players: game.players, turn: nextTurn(game.turn, game.players.length), sixCount: 0 };
    setGame(g);
    setValidMoves([]);
    setMessage(`${g.players[g.turn].name}'s turn — roll!`);
  }

  // CPU auto-play
  useEffect(() => {
    if (!game || game.winner !== null) return;
    const player = game.players[game.turn];
    if (!player.isCPU) return;

    if (!game.rolled) {
      cpuTimer.current = setTimeout(() => {
        const d = rollDice();
        const g = { ...game, dice: d, rolled: true, players: game.players };
        const moves = getValidMoves(g);
        setGame(g);
        setValidMoves(moves);
        if (moves.length === 0) {
          setMessage(`${player.name} rolled ${d} — no moves. Turn passes.`);
          cpuTimer.current = setTimeout(() => {
            handleAutoPass();
          }, 800);
        } else {
          setMessage(`${player.name} rolled ${d}. Thinking...`);
          cpuTimer.current = setTimeout(() => {
            const pick = moves[Math.floor(Math.random() * moves.length)];
            handleMove(pick);
          }, 600);
        }
      }, 500);
    }

    return () => { if (cpuTimer.current) clearTimeout(cpuTimer.current); };
  }, [game?.turn, game?.rolled, game?.winner]);

  // ---- render helpers -----------------------------------------------------

  function getPathCell(row: number, col: number): { pathIdx: number; color: PlayerColor | null } | null {
    const idx = PATH_GRID.findIndex((c) => c.row === row && c.col === col);
    if (idx === -1) return null;
    const color = ALL_COLORS.find((c) => isSafeZone(idx, c)) || null;
    return { pathIdx: idx, color };
  }

  function getHomeColCell(row: number, col: number): PlayerColor | null {
    for (const color of ALL_COLORS) {
      if (HOME_COLS[color].some((c) => c.row === row && c.col === col)) return color;
    }
    return null;
  }

  function getBaseCell(row: number, col: number): PlayerColor | null {
    for (const color of ALL_COLORS) {
      if (BASE_CELLS[color].some((c) => c.row === row && c.col === col)) return color;
    }
    return null;
  }

  function isCenter(row: number, col: number): boolean {
    return CENTER_CELLS.some((c) => c.row === row && c.col === col);
  }

  function tokenGridPos(token: { pos: number }, playerColor: PlayerColor): CellPos | null {
    if (token.pos === -1) return null; // base — rendered in base area
    if (token.pos === 57) return CENTER_CELLS[0]; // home
    if (token.pos >= 52) {
      // Home column
      const colIdx = token.pos - 52;
      return HOME_COLS[playerColor][colIdx] || null;
    }
    // Common path
    const globalIdx = (PLAYER_START[playerColor] + token.pos) % 52;
    return PATH_GRID[globalIdx] || null;
  }

  // ---- main render --------------------------------------------------------

  if (step === "setup") {
    return (
      <div className="game-shell">
        {!hideTitle && <h1 className="title">Ladoo</h1>}
        <section className="control-panel">
          <div className="row two-col">
            <label>Players <select value={playerCount} onChange={(e) => { const n = Number(e.target.value); setPlayerCount(n); setCpuCount(Math.min(cpuCount, n - 1)); }}>
              <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
            </select></label>
            <label>CPU Opponents <select value={cpuCount} onChange={(e) => setCpuCount(Number(e.target.value))}>
              {Array.from({ length: playerCount }, (_, i) => (<option key={i} value={i}>{i === 0 ? "None (all human)" : i === playerCount - 1 ? `vs ${i} CPU (1 human)` : `${i} CPU`}</option>))}
            </select></label>
          </div>
          <div className="row two-col">
            {Array.from({ length: playerCount }, (_, i) => (
              <label key={i}>{ALL_COLORS[i].toUpperCase()} Player
                <input value={names[i]} onChange={(e) => { const n = [...names]; n[i] = e.target.value; setNames(n); }} maxLength={16} placeholder={`Player ${i + 1}`} disabled={i >= playerCount - cpuCount && cpuCount > 0} />
              </label>
            ))}
          </div>
          <div className="actions"><button onClick={startGame}>Start Game</button></div>
        </section>
      </div>
    );
  }

  if (!game) return null;

  const currentPlayer = game.players[game.turn];
  const isYou = !currentPlayer.isCPU;

  return (
    <div className="game-shell">
      <ConfettiSplash
        active={game.winner !== null}
        winnerName={game.winner !== null ? game.players[game.winner].name : undefined}
      />
      {!hideTitle && <h1 className="title">Ladoo</h1>}

      <section className="control-panel">
        <p className="status" style={{ color: COLOR_HEX[currentPlayer.color] }}>
          {currentPlayer.name}'s turn
          {game.winner !== null ? ` — Winner!` : ""}
        </p>

        {isYou && game.winner === null && (
          <div className="ladoo-dice">
            {!game.rolled ? (
              <button className="ladoo-dice-btn" onClick={handleRoll}>🎲 Roll</button>
            ) : (
              <>
                <div className="ladoo-dice-result">🎲 {game.dice}</div>
                {validMoves.length > 0 ? (
                  <p className="subtle">Pick a token to move {game.dice} {game.dice === 1 ? "space" : "spaces"}</p>
                ) : (
                  <button onClick={handleAutoPass}>No moves — pass turn</button>
                )}
              </>
            )}
          </div>
        )}

        {message && <p className="subtle">{message}</p>}
        {capturedMsg && <p className="subtle" style={{ color: "#f88" }}>{capturedMsg}</p>}

        {isYou && game.winner === null && validMoves.length > 0 && (
          <div className="actions" style={{ gap: 8 }}>
            <span style={{ fontSize: "0.9rem", color: "#cde4ff" }}>Tokens:</span>
            {currentPlayer.tokens.map((t, i) => (
              <button
                key={t.id}
                className="ladoo-token-btn"
                onClick={() => handleMove(i)}
                disabled={!validMoves.includes(i)}
                title={t.pos === -1 ? "In base" : t.pos === 57 ? "Home" : `Position ${t.pos}`}
              >
                <span className={`ladoo-token ${currentPlayer.color}`} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, position: "static", opacity: validMoves.includes(i) ? 1 : 0.3, fontSize: 11, fontWeight: 700, color: "#fff" }}>{t.id + 1}</span>
              </button>
            ))}
          </div>
        )}

        {game.winner !== null && (
          <div className="actions">
            <button onClick={() => { setStep("setup"); setGame(null); }}>Play Again</button>
          </div>
        )}
      </section>

      <section className="board-panel">
        <div className="ladoo-board">
          {Array.from({ length: 15 }, (_, row) =>
            Array.from({ length: 15 }, (_, col) => {
              const path = getPathCell(row, col);
              const homeCol = getHomeColCell(row, col);
              const base = getBaseCell(row, col);
              const center = isCenter(row, col);

              let cls = "ladoo-cell";
              if (path) {
                cls += " path";
                if (path.color) cls += ` safe-${path.color}`;
              } else if (homeCol) {
                cls += ` home-col-${homeCol}`;
              } else if (base) {
                cls += ` base-${base}`;
              } else if (center) {
                cls += " center";
              }

              // Find tokens at this cell
              const tokensHere: { color: PlayerColor; id: number; isYou: boolean }[] = [];
              for (const p of game.players) {
                for (const t of p.tokens) {
                  const pos = tokenGridPos(t, p.color);
                  if (pos && pos.row === row && pos.col === col) {
                    tokensHere.push({ color: p.color, id: t.id, isYou: p === currentPlayer && isYou });
                  }
                }
              }

              return (
                <div key={`${row}-${col}`} className={cls} style={{ gridRow: row + 1, gridColumn: col + 1 }}>
                  {tokensHere.map((tk) => (
                    <span key={tk.id} className={`ladoo-token ${tk.color}${tk.isYou ? " you" : ""}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, color: "#fff" }}>{tk.id + 1}</span>
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* token bases */}
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
          {game.players.map((p) => (
            <div key={p.color} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "0.75rem", color: COLOR_HEX[p.color], fontWeight: 700 }}>{p.name}</div>
              <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                {p.tokens.filter((t) => t.pos === -1).map((t) => (
                  <span key={t.id} className={`ladoo-token ${p.color}`} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, position: "static", fontSize: 7, fontWeight: 700, color: "#fff" }}>{t.id + 1}</span>
                ))}
                {p.tokens.filter((t) => t.pos === -1).length === 0 && <span style={{ fontSize: "0.7rem", color: "#666" }}>all out</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Roll the dice on your turn.</p>
          <p>2. Roll a 6 to bring a token out of base or move 6 spaces.</p>
          <p>3. Land on an opponent's token to send it back to base.</p>
          <p>4. First player to get all 4 tokens home wins!</p>
          <p>5. Safe zones (colored path cells) protect from capture.</p>
        </div>
      </section>
    </div>
  );
}